import React, { useState } from 'react';
import styled from 'styled-components';
import { ChevronDown } from 'lucide-react';

const SectionCard = styled.section`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  margin-bottom: ${props => props.theme.spacing.md};
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.md};
  ${props => props.$collapsible && `
    cursor: pointer;
    user-select: none;
    &:hover { background: ${props.theme.colors.background}; }
  `}
`;

const IconBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.tint}20;
  color: ${props => props.theme.colors.tint};
  flex-shrink: 0;
`;

const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Title = styled.div`
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: ${props => props.theme.typography.weights.semiBold};
  color: ${props => props.theme.colors.text};
  line-height: 1.3;
`;

const Description = styled.div`
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
  line-height: 1.4;
`;

const Chevron = styled(ChevronDown)`
  color: ${props => props.theme.colors.muted};
  transition: transform 0.2s ease;
  transform: rotate(${props => props.$open ? '0deg' : '-90deg'});
  flex-shrink: 0;
`;

const Body = styled.div`
  padding: 0 ${props => props.theme.spacing.md} ${props => props.theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.border};
  padding-top: ${props => props.theme.spacing.md};
`;

export const PropertySection = ({
  icon: Icon,
  title,
  description,
  collapsible = false,
  defaultOpen = true,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = () => collapsible && setOpen(v => !v);

  return (
    <SectionCard>
      <Header
        $collapsible={collapsible}
        onClick={toggle}
        role={collapsible ? 'button' : undefined}
        aria-expanded={collapsible ? open : undefined}
        tabIndex={collapsible ? 0 : undefined}
        onKeyDown={collapsible ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } } : undefined}
      >
        {Icon && (
          <IconBadge>
            <Icon size={16} />
          </IconBadge>
        )}
        <TitleBlock>
          <Title>{title}</Title>
          {description && <Description>{description}</Description>}
        </TitleBlock>
        {collapsible && <Chevron size={18} $open={open} />}
      </Header>
      {(!collapsible || open) && <Body>{children}</Body>}
    </SectionCard>
  );
};
